<!-- Formatted display of a parsed error stack trace with clickable file links. -->

<script lang="ts" setup>
import { parseStackTrace } from '@nzyme/utils/parseStackTrace.js';
import { defineProp } from '@nzyme/vue-utils/defineProp.js';
import { computed } from 'vue';

const props = defineProps({
  stack: defineProp<string>({ required: true }),
  title: defineProp<string>(),
});

const frames = computed(() => formatStackTrace(props.stack));

function formatStackTrace(stack: string) {
  const baseFrames = parseStackTrace(stack);

  return baseFrames.map(frame => {
    if (frame.type === 'function') {
      const filePath = frame.filePath;

      let fileLink: string | undefined;
      if (filePath.startsWith('/')) {
        fileLink = `cursor://file${filePath}`;
      }

      return {
        type: 'function' as const,
        name: frame.name,
        fileLink: fileLink ?? undefined,
        filePath,
      };
    }

    return frame;
  });
}
</script>

<template>
  <div :class="css.stackTrace">
    <h2
      v-if="title"
      :class="css.stackTraceTitle"
    >
      {{ title }}
    </h2>
    <div :class="css.stackTraceContent">
      <div
        v-for="(frame, index) in frames"
        :key="index"
        :class="css.stackTraceLine"
      >
        <template v-if="frame.type === 'function'">
          <span :class="css.stackTraceAt">at</span>
          <span :class="css.stackTraceFunction">{{ frame.name }}</span>
          <span :class="css.stackTraceLocation">
            (
            <a
              v-if="frame.fileLink"
              :href="frame.fileLink"
              :class="css.stackTraceLink"
            >
              {{ frame.filePath }}
            </a>
            <template v-else> {{ frame.filePath }} </template>
            )
          </span>
        </template>
        <template v-else-if="frame.type === 'simple'">
          <span :class="css.stackTraceAt">at</span>
          <span :class="css.stackTraceText">{{ frame.text }}</span>
        </template>
        <template v-else>
          <span :class="css.stackTraceText">{{ frame.text }}</span>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module="css">
.stackTrace {
  width: 100%;
  background: $color-white;
  border: 1px solid $color-gray-300;
  border-radius: $border-radius-08;
  overflow: hidden;
}

.stackTraceTitle {
  font-size: $font-size-14;
  font-weight: $font-weight-600;
  color: $color-gray-700;
  padding: $spacing-12 $spacing-16;
  margin: 0;
  background: $color-gray-50;
  border-bottom: 1px solid $color-gray-300;
}

.stackTraceContent {
  padding: $spacing-12 $spacing-16;
  max-height: 400px;
  overflow-y: auto;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: $font-size-12;
  line-height: 1.6;
}

.stackTraceLine {
  padding: $spacing-04 0;
  color: $color-gray-700;
  white-space: nowrap;

  &:not(:last-child) {
    border-bottom: 1px solid $color-gray-100;
  }
}

.stackTraceAt {
  color: $color-gray-600;
  margin-right: $spacing-08;
}

.stackTraceFunction {
  color: $color-blue-600;
  font-weight: $font-weight-600;
  margin-right: $spacing-08;
}

.stackTraceLocation {
  color: $color-gray-600;
}

.stackTraceText {
  color: $color-gray-600;
}

.stackTraceLink {
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: $color-blue-600;
  }
}
</style>
